import React, { Component } from 'react';
import constants from '../../util/constants';
import axios from 'axios';
import { getBudgetWarningText, getCapacityWarningText } from '..';
import { FormGroup, InputGroup, FormControl } from 'react-bootstrap';
import FontAwesome from 'react-fontawesome';
import ReactTooltip from 'react-tooltip';
import './style.scss';

const { API_BASE_URL } = constants;

class EditedAllocationWarnings extends Component {
  constructor(props) {
    super(props);
    this.state = {
      header: this.props.apiToken ? {
                'Authorization': 'Bearer ' + this.props.apiToken,
                'Accept': 'application/octet-stream'
      } : {},
      tolerance: '0,5',
      showHistorical: false,
    };
  }

  handleEditedAllocationsChange(newEditedAllocations) {
    this.props.setVerifyingAllocationCount(true);
    axios.post(`${API_BASE_URL}Allocation/VerifyEditedAllocations`, {searchObject : this.props.searchObject, editedAllocations: newEditedAllocations}, { headers: this.state.header })
      .then(response => {
        const camelCaseData = this.transformToCamelCase(response.data);
        this.props.onWarningAllocationsChange(camelCaseData);
        this.props.setVerifyingAllocationCount(false);
      })
      .catch(error => {
        console.error("Verification failed", error);
        this.props.setVerifyingAllocationCount(false);
      });
  }

  transformToCamelCase = (data) => {
    return data.map(item => ({
      allocationId: item.AllocationId,
      month: item.Month,
      allocated: item.Allocated,
      originalAllocated: item.OriginalAllocated,
      pm: item.Pm,
      grade: item.Grade,
      country: item.Country,
      customer: item.Customer,
      agent: item.Agent,
      areaSalesDirector: item.AreaSalesDirector,
      capacityWarning: item.CapacityWarning ? {
        allocated: item.CapacityWarning.Allocated,
        limit: item.CapacityWarning.Limit,
        isActualCapacity: item.CapacityWarning.IsActualCapacity
      } : null,
      budgetAllocationWarning: item.BudgetAllocationWarning ? {
        allocated: item.BudgetAllocationWarning.Allocated,
        limit: item.BudgetAllocationWarning.Limit,
        isActualCapacity: item.BudgetAllocationWarning.IsActualCapacity
      } : null,
    }));
  }

  initialVerifyAllocations = () => {
    this.props.setVerifyingAllocationCount(true);
    axios.get(`${API_BASE_URL}Allocation/InitialVerifyAllocations`, { headers: this.state.header })
      .then(response => {
        const camelCaseData = this.transformToCamelCase(response.data);
        this.props.onEditedAllocationsChange(camelCaseData);
        this.props.setVerifyingAllocationCount(false);
      })
      .catch(error => {
        console.error("Verification failed", error);
        this.props.setVerifyingAllocationCount(false);
      });
  }

  componentDidMount() {
    this.initialVerifyAllocations();
    if (this.props.editedAllocations && this.props.editedAllocations.length > 0) {
      this.handleEditedAllocationsChange(this.props.editedAllocations);
    }
  }

  componentDidUpdate(prevProps) {
    if (this.props.editedAllocations !== prevProps.editedAllocations){
      this.handleEditedAllocationsChange(this.props.editedAllocations);
    }
    if (this.props.editMode && !prevProps.editMode) {
      this.setState({ tolerance: '0' });
    }
  }

  getDistinctWarnings(warnings, distinctKeys) {
    const seen = new Set();
    return warnings.filter(x => {
      const key = distinctKeys.map(k => x[k]).join('_');
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }

  warningIsNotExpired(warning) {
    //Filter displayed warnings according to if historic warnings should be shown or not
    const { searchedYear } = this.props;
    const currentMonth = new Date().getMonth() + 1; //getMonth is zero-based (+1 to match backend-data)
    const currentYear = new Date().getFullYear();
    return (searchedYear.value === currentYear && warning.month >= currentMonth) || searchedYear.value > currentYear;
  }

  warningExceedsTolerance(warning) {
    //Filter warnings according to tolerance. If tolerance is 10 then only disply warnings where allocated volume is over 10% of actual capacity 
    const toleranceMultiplier = 1 + Number(this.state.tolerance.replace(',', '.')) / 100;
    return warning.allocated > warning.limit * toleranceMultiplier;
  }

  filterWarnings(warningKey, distinctKeys = null) {
    let result = this.props.warningAllocations.filter(x => x[warningKey] != null);
    if (distinctKeys){
      result = this.getDistinctWarnings(result, distinctKeys);
    }
    result = result.filter(x => this.warningExceedsTolerance(x[warningKey]));
    return result.filter(x => this.state.showHistorical || this.warningIsNotExpired(x));
  }

  render() {
    const { showHistorical } = this.state;
    const filteredPmWarnings = this.filterWarnings('capacityWarning', ['pm', 'month']);
    const filteredCustomerWarnings = this.filterWarnings('budgetAllocationWarning').sort((a, b) => a.month - b.month);
    const infoCapacityWarnings = `
      <div>
        PM Warnings:<br/>
        Warnings where the allocated volume for a machine in a given month<br/>
        exceeds the actual available machine capacity, adjusted for the configured tolerance.<br/>
        <strong>Note:</strong> If the actual capacity for a month is 0, the budget capacity is used as the capacity reference instead.<br/><br/>

        <strong>Tolerance:</strong><br/>
        <div style="padding-left: 20px;">
          • Set tolerance to allow a percentage buffer before warnings appear.<br/>
          • Example: tolerance 0,5 means warnings only show when allocation exceeds capacity/budget by more than 0,5%.<br/>
          • Set tolerance to 0 to show all warnings.<br/>
        </div><br/>
        <strong>Expired:</strong><br/>
        <div style="padding-left: 20px;">
          • When checked, warnings for past months are also displayed.<br/>
          • When unchecked, only warnings for the current and future months are shown.<br/>
        </div>
      </div>`;

    const infoCustomerWarnings = `
        <div>
          Customer Warnings:<br/>
          Warnings where the allocated volume for a customer in a given month<br/>
          exceeds the budget allocated volume, adjusted for the configured tolerance.<br/><br/>
        </div>
    `;
    return (
      <div>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <span className="allocationLegend" style={{ color: 'red', margin: 0, padding: 0, display: 'flex', alignItems: 'center', gap: '5px' }}>
            {'PM Warnings:'}
            <FontAwesome
              name="info-circle"
              data-for="warningTip"
              data-tip={infoCapacityWarnings}
              style={{ cursor: 'pointer', fontSize: '16px', color: '#000000', marginRight: 5 }}
            />
            <ReactTooltip id="warningTip" html place="bottom" effect="solid" />
          </span>
          <FormGroup style={{ marginBottom: 0, display: 'flex' }}>
            <InputGroup bsSize="small">
              <InputGroup.Addon>Tolerance</InputGroup.Addon>
              <FormControl
                type='text'
                step='any'
                value={this.state.tolerance}
                style={{ width: '60px'}}
                onInput={(e) => e.target.value = e.target.value.replace(/[^0-9,]/g, '')}
                onChange={evt => {
                  this.setState({ tolerance: evt.target.value.replace(/[^0-9,]/g, '') });
                }}
              />
            </InputGroup>
            <InputGroup bsSize="small">
              <InputGroup.Addon>Expired</InputGroup.Addon>
              <input
                type="checkbox"
                checked={showHistorical}
                onChange={e => this.setState({ showHistorical: e.target.checked })}
              />
            </InputGroup>
          </FormGroup>
        </div>
        <div
          style={{
            maxHeight: '195px',
            overflowY: 'auto',
            marginTop: '5px',
            border: '1px solid #ddd',
          }}
        >
          <ul>
            {filteredPmWarnings.map((warning, id) => (
              <li key={id}>{getCapacityWarningText(warning)}</li>
            ))}
          </ul>
        </div>
        <span className="allocationLegend" style={{ color: 'red', margin: 0, marginTop: 5, padding: 0, display: 'flex', alignItems: 'center', gap: '5px' }}>
            {'Customer Warnings:'}
            <FontAwesome
              name="info-circle"
              data-for="warningTip"
              data-tip={infoCustomerWarnings}
              style={{ cursor: 'pointer', fontSize: '16px', color: '#000000', marginRight: 5 }}
            />
            <ReactTooltip id="warningTip" html place="bottom" effect="solid" />
        </span>
        <div
          style={{
            maxHeight: '190px',
            overflowY: 'auto',
            marginTop: '5px',
            border: '1px solid #ddd',
          }}
        >
          <ul>
            {filteredCustomerWarnings.map((warning, id) => (
              <li key={id}>{getBudgetWarningText(warning, this.props.areaSalesDirectors)}</li>
            ))}
          </ul>
        </div>
      </div>
    );
  }
}

export default EditedAllocationWarnings;